/**
 * Help content for ArchiMate Advanced Feature Panels
 * Provides guidance on how to use each panel effectively
 */

export interface HelpContent {
  title: string;
  content: string;
}

export const PANEL_HELP: Record<string, HelpContent> = {
  adr: {
    title: 'Architecture Decision Records (ADRs)',
    content: `**Document Important Architecture Decisions:**

**What are ADRs?** Architecture Decision Records capture significant architectural decisions, their context, and consequences. They create a historical record of "why" decisions were made.

**How to Create an ADR:**
1. Click "New ADR" button in the toolbar
2. Choose a template (Nygard, MADR, Y-Statement) or start blank
3. Fill in the sections:
   • **Title**: Brief decision summary (e.g., "Use PostgreSQL for primary database")
   • **Context**: What circumstances led to this decision?
   • **Decision**: What did you decide to do?
   • **Consequences**: What are the positive and negative outcomes?
   • **Alternatives**: What other options were considered?
4. Click "Create ADR" to save

**Managing ADRs:**
- **Change Status**: Click on an ADR and update its status (Proposed → Accepted → Deprecated/Superseded)
- **Timeline View**: Switch to Timeline view to see decisions chronologically
- **Export**: Click "Export" to download all ADRs as markdown files
- **Search**: Use the search bar to find specific decisions

**Pro Tip:** Link ADRs to specific elements in your diagram by mentioning component names in the decision text.`,
  },
  cost: {
    title: 'Cost & Total Cost of Ownership',
    content: `**Track and Optimize Infrastructure Costs:**

**How to Add Cost Information:**
1. Select "Element" tab to add costs to specific components
2. Click an element in your diagram to select it
3. Enter costs for each category:
   • **Infrastructure**: Hosting, servers, cloud resources
   • **Licensing**: Software licenses, subscriptions
   • **Team**: Personnel costs (salary, contractors)
   • **Maintenance**: Support contracts, updates
   • **Other**: Miscellaneous costs
4. Set implementation cost if this is a new component
5. Choose currency (USD, EUR, GBP) and period (monthly/annual)
6. Click "Save" to apply

**Viewing Cost Analysis:**
- **Overview Tab**: See total costs across your entire architecture
- **By Layer**: View costs broken down by architectural layer
- **Top Costs**: Identify your 5 most expensive components
- **Heatmap Toggle**: Enable heatmap to color-code elements by cost intensity

**Export:**
- Click "Export CSV" to download detailed cost breakdown for spreadsheet analysis

**Pro Tip:** Start with infrastructure and licensing costs first as they're usually the largest expenses.`,
  },
  security: {
    title: 'Security & Compliance',
    content: `**Assess and Track Security Posture:**

**How to Add Security Information:**
1. Select "Element" tab to assess individual components
2. Click an element in your diagram to select it
3. Set the **Security Risk Level**:
   • **Low**: Standard security controls, low exposure
   • **Medium**: Moderate risk, requires attention
   • **High**: Critical components, extensive controls needed
4. Check applicable **Compliance Requirements**:
   • SOC 2, GDPR, HIPAA, PCI-DSS, ISO 27001, etc.
5. Document **Security Controls** in place:
   • Encryption, authentication, access controls, monitoring
6. Add notes about vulnerabilities or pending security work
7. Click "Save" to apply

**Viewing Security Analysis:**
- **Overview Tab**: See security risk distribution across architecture
- **High Risk Elements**: List of components requiring immediate attention
- **Compliance Summary**: Which components need specific compliance
- **Heatmap Toggle**: Enable heatmap to visualize security risk levels

**Best Practice:**
1. Start with data stores and APIs that handle sensitive information
2. Work outward to their dependencies
3. Ensure all components processing PII are documented
4. Review and update quarterly

**Pro Tip:** Use different colors in the heatmap to quickly identify your attack surface during architecture reviews.`,
  },
  debt: {
    title: 'Technical Debt Tracking',
    content: `**Identify and Prioritize Technical Debt:**

**How to Track Technical Debt:**
1. Select "Element" tab to add debt to components
2. Click an element in your diagram
3. Click "Add Debt Item" button
4. Fill in debt details:
   • **Category**: Code Quality, Documentation, Testing, Performance, Security
   • **Description**: What needs to be fixed?
   • **Priority**: Low, Medium, High, Critical
   • **Effort**: Estimated hours or days to resolve
   • **Impact**: How much does this affect the system?
5. Click "Save" to add the debt item
6. Repeat for additional debt items on the same component

**Managing Debt:**
- **Overview Tab**: See total debt across architecture
- **By Priority**: View critical items first
- **By Category**: Group debt by type (code, docs, testing, etc.)
- **Heatmap**: Visualize which components have the most debt
- **Mark Resolved**: Check off items as they're fixed

**Prioritization Strategy:**
- **Quick Wins**: High impact + Low effort (do these first!)
- **Major Projects**: High impact + High effort (plan for these)
- **Fill-ins**: Low impact + Low effort (do when you have time)
- **Defer**: Low impact + High effort (consider not doing)

**Pro Tip:** Review technical debt during sprint planning to allocate 20% of each sprint to debt reduction.`,
  },
  validation: {
    title: 'Validation Rules & Architecture Governance',
    content: `**Enforce Architecture Standards:**

**How to Use Validation:**
1. Open the Validation panel from Advanced Features menu
2. Click "Run Validation" to check your diagram
3. Review the violations list showing:
   • Violation type and severity
   • Affected elements
   • Suggested fixes
4. Click on a violation to see details and jump to the element
5. Fix the issue in your diagram
6. Re-run validation to verify the fix

**Common Violations:**
- **Invalid Relationships**: Connections that don't follow ArchiMate rules (e.g., can't connect business to technology directly)
- **Naming Issues**: Inconsistent naming conventions, missing element names
- **Layer Violations**: Cross-layer dependencies that violate separation of concerns
- **Missing Documentation**: Elements without descriptions or properties

**Fixing Violations:**
- **Quick Fix**: Some violations have auto-fix buttons
- **Manual Fix**: Edit the element or relationship to comply with rules
- **Suppress**: Mark false positives as "Accepted" if intentional

**Custom Rules:**
- Click "Configure Rules" to enable/disable specific validations
- Add organization-specific naming conventions
- Set required properties for elements

**Pro Tip:** Run validation after every major change and before presenting to stakeholders.`,
  },
  performance: {
    title: 'Performance & SLOs',
    content: `**Track Performance Metrics and Service Level Objectives:**

**How to Add Performance Metrics:**
1. Select "Element" tab
2. Click a component in your diagram
3. Define performance metrics:
   • **Response Time**: Target latency (e.g., "< 200ms p95")
   • **Throughput**: Requests per second (e.g., "1000 req/s")
   • **Availability**: Uptime SLA (e.g., "99.9%")
   • **Error Rate**: Acceptable failure rate (e.g., "< 0.1%")
4. Set **Current** vs. **Target** values
5. Add **SLO Details**: How is this measured? What's the penalty for missing?
6. Click "Save"

**Viewing Performance Data:**
- **Overview**: See all components with defined SLOs
- **At Risk**: Components not meeting their targets
- **Heatmap**: Visualize performance issues
- **Trends**: Track improvements over time (if integrated with monitoring)

**Best Practices:**
1. Start with user-facing APIs and pages (frontend)
2. Define SLOs based on user experience requirements
3. Work backwards through dependencies
4. Set realistic targets based on current performance
5. Review and adjust quarterly

**Common SLO Patterns:**
- **Public APIs**: 99.9% availability, <500ms p95
- **Internal Services**: 99.5% availability, <1s p95
- **Batch Jobs**: Complete within SLA window, <1% failure rate

**Pro Tip:** Link performance metrics to cost data to identify expensive components that don't meet performance targets.`,
  },
  team: {
    title: 'Team Ownership & Responsibilities',
    content: `**Map Team Ownership and Responsibilities:**

**How to Define Teams:**
1. Click "Create Team" in the Team panel
2. Enter team details:
   • **Team Name**: e.g., "Platform Team", "Data Team"
   • **Description**: Team's mission and scope
   • **Contact**: Lead engineer, Slack channel, email
   • **Members**: Team size (optional)
3. Click "Save Team"
4. Repeat for all teams in your organization

**Assigning Ownership:**
1. Select "Assign Components" mode
2. Click elements in your diagram
3. Choose the owning team from the dropdown
4. Click "Assign" to save
5. Multiple elements can be assigned at once using multi-select

**Viewing Ownership:**
- **By Team**: Filter diagram to show only one team's components
- **Ownership Matrix**: Table view of which team owns what
- **Unassigned**: List of components with no owner (fix these!)
- **Cross-team Dependencies**: See where teams depend on each other

**Best Practices:**
- Every component should have exactly one owner
- Teams own components they deploy and maintain
- Shared libraries can be owned by platform teams
- Update ownership when teams reorganize

**Pro Tip:** Use the "Unassigned Components" view during onboarding to help new team members understand the landscape.`,
  },
  changelog: {
    title: 'Change History & Version Tracking',
    content: `**Track Architecture Evolution Over Time:**

**Change Log:** Automatically track changes to your architecture with detailed history.

**Features:**
- **Automatic Tracking**: Changes logged automatically
- **Change Types**: Added, modified, removed elements/relationships
- **Timeline View**: See evolution chronologically
- **Diff Comparison**: Compare versions
- **Audit Trail**: Who changed what and when

**Use Cases:**
- Understanding architecture evolution
- Compliance audits
- Root cause analysis
- Team communication

**Pro Tip:** Review the changelog during retrospectives to understand how your architecture is evolving and identify patterns.`,
  },
  scenario: {
    title: 'Scenario Planning & What-If Analysis',
    content: `**Model Future States and Alternatives:**

**Scenario Planning:** Create alternative versions of your architecture to explore different approaches.

**Features:**
- **Scenario Creation**: Define future states or alternatives
- **Element Visibility**: Show/hide elements per scenario
- **Comparison Views**: Compare scenarios side-by-side
- **Migration Paths**: Visualize transitions between states
- **Decision Support**: Evaluate trade-offs

**Use Cases:**
- Migration planning (current → future state)
- Technology evaluation (Option A vs. Option B)
- Disaster recovery planning
- Capacity planning

**Pro Tip:** Create "as-is" and "to-be" scenarios to communicate transformation roadmaps to stakeholders.`,
  },
  migration: {
    title: 'Migration Planning & Roadmaps',
    content: `**Plan Complex Migrations and Transformations:**

**Migration Planning:** Break down large migrations into phased steps with dependencies.

**Features:**
- **Phase Definition**: Define migration phases (e.g., Phase 1, 2, 3)
- **Element Mapping**: Assign elements to phases
- **Dependency Tracking**: Ensure phases execute in correct order
- **Risk Assessment**: Identify migration risks
- **Timeline Visualization**: See migration roadmap

**Use Cases:**
- Cloud migrations
- Monolith to microservices transformations
- Data center moves
- Technology stack upgrades

**Pro Tip:** Work backward from your end goal - define the final state first, then plan the incremental steps to get there.`,
  },
  stakeholder: {
    title: 'Stakeholder Views & Perspectives',
    content: `**Create Targeted Views for Different Audiences:**

**Stakeholder Views:** Generate simplified, role-specific views from your comprehensive architecture.

**View Types:**
- **Executive View**: High-level capabilities and business value
- **Developer View**: Technical details and dependencies
- **Operations View**: Infrastructure and deployment
- **Security View**: Security controls and risk
- **Custom Views**: Define your own perspectives

**Features:**
- **Automatic Filtering**: Show only relevant elements
- **Abstraction Levels**: Adjust detail level
- **Export Views**: Share with stakeholders
- **View Templates**: Reuse common patterns

**Pro Tip:** Create stakeholder-specific views before meetings to ensure the right level of detail for each audience.`,
  },
  infrastructure: {
    title: 'Infrastructure Import from Cloud',
    content: `**Import Existing Infrastructure:**

**Cloud Import:** Automatically generate architecture diagrams from existing cloud infrastructure.

**Supported Sources:**
- AWS (CloudFormation, CDK)
- Azure (ARM Templates, Bicep)
- GCP (Deployment Manager)
- Terraform/OpenTofu
- Kubernetes (YAML manifests)

**Features:**
- **Automatic Discovery**: Import existing resources
- **Relationship Detection**: Auto-create connections
- **Metadata Import**: Include tags, properties
- **Continuous Sync**: Keep diagrams updated

**Use Cases:**
- Documenting existing systems
- Migration planning
- Disaster recovery planning
- Compliance documentation

**Pro Tip:** Import your production infrastructure first, then use it as a baseline for planning migrations or improvements.`,
  },
  'view-generator': {
    title: 'View Generator',
    content: `**Generate Specialized Views Automatically:**

**View Generator:** Create focused architectural views from your comprehensive model.

**Generated Views:**
- **Layered View**: Organize by architectural layers
- **Dependency View**: Focus on relationships
- **Component View**: Specific component and its context
- **Integration View**: External system integrations
- **Data Flow View**: How data moves through the system

**Features:**
- **Automatic Layout**: Smart positioning
- **Filter Options**: Include/exclude elements
- **Export Views**: Save as separate diagrams
- **View Templates**: Reusable view configurations

**Pro Tip:** Use generated views to create documentation for specific use cases without cluttering your main diagram.`,
  },
  sequence: {
    title: 'Sequence Diagrams & Flows',
    content: `**Visualize Dynamic Behavior:**

**Sequence Diagrams:** Show how components interact over time for specific use cases.

**Features:**
- **Message Sequences**: Define interactions between components
- **Timing Diagrams**: Show temporal relationships
- **Mermaid Export**: Generate Mermaid.js diagrams
- **Use Case Mapping**: Link to specific scenarios

**Use Cases:**
- API interaction flows
- Authentication sequences
- Data processing pipelines
- Error handling flows
- User journey mapping

**Pro Tip:** Create sequence diagrams for your most critical user journeys to ensure all components interact correctly.`,
  },
  keyboard: {
    title: 'Keyboard Shortcuts',
    content: `**Work Faster with Keyboard Shortcuts:**

**Navigation & Selection:**
- **V**: Select mode
- **C**: Connect mode
- **B**: Browse diagrams
- **L**: Component library
- **Escape**: Cancel/deselect

**Editing:**
- **Ctrl/Cmd + C**: Copy element
- **Ctrl/Cmd + V**: Paste element
- **Ctrl/Cmd + D**: Duplicate element
- **Delete/Backspace**: Delete selected
- **Ctrl/Cmd + A**: Select all

**Diagram Management:**
- **Ctrl/Cmd + S**: Save diagram
- **Ctrl/Cmd + E**: Export PNG
- **Ctrl/Cmd + F**: Search (Query Bar)

**Pro Tip:** Press **?** to see this shortcuts panel anytime.`,
  },
};
